import { formatFileSize } from "@utils/common";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutationData } from "../../hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { OutlineButton, PrimaryButton } from "../Buttons";
import { ComponentTable } from "../Tables";
import SbomItemsList from "./SbomItemsList";
import useEscapeKey from "@hooks/useEscapeKey";

const SbomComparisonResultModal = ({
  show,
  onHide,
  sbomVerificationSeq,
  binaryVerificationSeq,
  onSelectOtherVerification,
  onDownloadComparisonResult,
  licensePolicyData,
  "data-testid": dataTestId = "sbom-comparison-result-modal",
}) => {
  const fieldContainerTestId = (name) => `${dataTestId}-field-${name}-container`;
  const fieldLabelTestId = (name) => `${dataTestId}-field-${name}-label`;
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { t } = useTranslation();
  const { colors } = useTheme();

  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verificationDetails, setVerificationDetails] = useState(null);
  const [missingComponents, setMissingComponents] = useState([]);
  const [clarityScanComponents, setClarityScanComponents] = useState([]);
  const [unverifiedComponents, setUnverifiedComponents] = useState([]);

  // Download comparison result mutation
  const downloadComparisonResultMutation = useMutationData(
    "/sbomVerificationDownloadCompareResult",
    {
      method: "GET",
      responseType: "blob",
      params: {
        sbomVerificationSeq: sbomVerificationSeq,
        binaryVerificationSeq: binaryVerificationSeq,
      },
    },
    {
      onSuccess: (response) => {
        if (response instanceof Blob) {
          const url = window.URL.createObjectURL(response);
          const a = document.createElement("a");
          a.href = url;
          a.download = `comparison_result_${sbomVerificationSeq}_${binaryVerificationSeq}.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);

          // Close the modal after successful download
          handleClose();
        }
      },
      onError: (error) => {
        console.error("Error downloading comparison result:", error);
      },
    },
  );

  // Handle download button click
  const handleDownloadComparisonResult = () => {
    if (sbomVerificationSeq && binaryVerificationSeq) {
      downloadComparisonResultMutation.mutate();
    }
  };

  useEscapeKey(onHide, show);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
      fetchComparisonData();
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show, sbomVerificationSeq, binaryVerificationSeq]);

  // API mutation for comparison
  const getComparisonMutation = useMutationData(
    (binaryVerificationSeq) => `/sbom_verification_compare_to_binary/${sbomVerificationSeq}/${binaryVerificationSeq}`,
    { method: "GET" },
    {
      onSuccess: (response) => {
        setComparisonData(response);
        setVerificationDetails(response.binaryVerification);
        setMissingComponents(response.missings);
        setClarityScanComponents(response.binaryScanComponents);
        setUnverifiedComponents(response.notApplicables);
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching comparison data:", error);
        setLoading(false);
      },
    },
  );

  // Fetch comparison data
  const fetchComparisonData = () => {
    if (!sbomVerificationSeq || !binaryVerificationSeq) return;

    setLoading(true);
    getComparisonMutation.mutate(binaryVerificationSeq);
  };

  const handleClose = () => {
    onHide();
  };

  const handleSelectOtherVerification = () => {
    onSelectOtherVerification();
  };

  // Helper function to format components for display
  const formatComponentForDisplay = (component) => {
    if (typeof component === "string") {
      return component;
    }
    if (component.componentName && component.componentVersion) {
      return `${component.componentName} (${component.componentVersion})`;
    }
    return component.toString();
  };

  // Helper function to get license name
  const getLicenseName = (component) => {
    if (component.licenseName) {
      return component.licenseName;
    }
    return "---";
  };

  if (!show) return null;

  return (
    <div className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start xl:justify-center p-4 backdrop-blur-[1px] overflow-x-auto`} data-testid={`${dataTestId}-overlay`}>
      <div className={`${colors.bgModal} w-[80vw] min-w-[1200px] max-h-[90vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl xl:overflow-y-hidden flex flex-col`} data-testid={dataTestId}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 border-b ${colors.borderPrimary}`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold`} data-testid={`${dataTestId}-title`}>
              {t("project.sbom.sbom-verification-comparison")}
            </h2>
            <OutlineButton
              onClick={handleSelectOtherVerification}
              className="flex items-center space-x-2"
            >
              {t("project.sbom.select-other-verification")}
            </OutlineButton>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 pb-4 flex-1 min-h-0 overflow-y-auto space-y-6" data-testid={`${dataTestId}-content`}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${colors.borderLoader}`}></div>
            </div>
          ) : comparisonData ? (
            <>
              {/* Verification Details Section */}
              <div
                className={`rounded-2xl border ${colors.borderPrimary} ${colors.bgSecondary} p-6 transition-all duration-200 hover:shadow-lg`}
              >
                <div className="grid grid-cols-4 gap-6">
                  <div data-testid={fieldContainerTestId("verif-real-file-name")}>
                    <span className={`text-sm ${colors.textSecondary} block mb-1`} data-testid={fieldLabelTestId("verif-real-file-name")}>
                      {t("project.sbom.verif-real-file-name")}
                    </span>
                    <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("verif-real-file-name")}>
                      {verificationDetails?.realFileName || "---"}
                    </span>
                  </div>
                  <div data-testid={fieldContainerTestId("verif-type")}>
                    <span className={`text-sm ${colors.textSecondary} block mb-1`} data-testid={fieldLabelTestId("verif-type")}>{t("project.sbom.verif-type")}</span>
                    <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("verif-type")}>
                      {t("project.sbom.binary-verif")}
                    </span>
                  </div>
                  <div data-testid={fieldContainerTestId("version")}>
                    <span className={`text-sm ${colors.textSecondary} block mb-1`} data-testid={fieldLabelTestId("version")}>{t("common.version")}</span>
                    <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("version")}>
                      {verificationDetails?.verificationVersion || "---"}
                    </span>
                  </div>
                  <div data-testid={fieldContainerTestId("total-file-size")}>
                    <span className={`text-sm ${colors.textSecondary} block mb-1`} data-testid={fieldLabelTestId("total-file-size")}>
                      {t("project.sbom.total-file-size")}
                    </span>
                    <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("total-file-size")}>
                      {formatFileSize(verificationDetails?.totalFileSize) || "---"}
                    </span>
                  </div>
                  <div data-testid={fieldContainerTestId("detected-component")}>
                    <span className={`text-sm ${colors.textSecondary} block mb-1`} data-testid={fieldLabelTestId("detected-component")}>
                      {t("project.sbom.detected-component")}
                    </span>
                    <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("detected-component")}>
                      {verificationDetails?.componentVersionCnt || "---"}
                    </span>
                  </div>
                  <div data-testid={fieldContainerTestId("requester")}>
                    <span className={`text-sm ${colors.textSecondary} block mb-1`} data-testid={fieldLabelTestId("requester")}>{t("common.requester")}</span>
                    <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("requester")}>
                      {verificationDetails?.requesterName || "---"}
                    </span>
                  </div>
                  <div data-testid={fieldContainerTestId("requested-at")}>
                    <span className={`text-sm ${colors.textSecondary} block mb-1`} data-testid={fieldLabelTestId("requested-at")}>{t("common.requested-at")}</span>
                    <span className={`text-sm font-medium ${colors.textPrimary}`} data-testid={fieldValueTestId("requested-at")}>
                      {verificationDetails?.requestedAt
                        ? new Date(verificationDetails.requestedAt)
                          .toLocaleString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                            hour12: false,
                          })
                          .replace(/(\d+)\/(\d+)\/(\d+)/, "$3-$1-$2")
                        : "---"}
                    </span>
                  </div>
                </div>
                <SbomItemsList
                  missings={missingComponents}
                  notApplicables={unverifiedComponents}
                  onDownload={() => { }}
                  itemsPerPage={5}
                  isDisplayDownloadButton={false}
                  firstColumnTitle={t("project.sbom.component-not-found-clarity")}
                  secondColumnTitle={t("project.sbom.component-not-found-sbom")}
                />
                <div className="pt-6">
                  <h3 className={`text-lg font-semibold ${colors.textPrimary} mb-4`}>
                    {t("project.sbom.clarity-scan-components-list")}
                  </h3>
                  {clarityScanComponents && clarityScanComponents.length > 0 ? (
                    <ComponentTable
                      data={clarityScanComponents}
                      licensePolicyData={licensePolicyData || []}
                      onDataChange={() => { }}
                      seq="componentSeq"
                      className="p-3"
                      isEditingAllowed={false}
                    />
                  ) : (
                    <p className={`text-sm ${colors.textSecondary}`} data-testid={fieldValueTestId("no-clarity-scan-components")}>{t("project.sbom.no-clarity-scan-components")}</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className={`text-lg ${colors.textSecondary}`} data-testid={fieldValueTestId("no-comparison-data")}>{t("project.sbom.no-comparison-data-available")}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 border-t ${colors.borderPrimary} flex-shrink-0`} data-testid={`${dataTestId}-footer`}>
          <div className="flex justify-end gap-4">
            <OutlineButton data-testid={`${dataTestId}-close-button`} onClick={handleClose}>{t("common.close-button")}</OutlineButton>
            <PrimaryButton data-testid={`${dataTestId}-confirm-button`} onClick={handleDownloadComparisonResult} disabled={!comparisonData}>
              {t("project.sbom.download-comparison-result")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SbomComparisonResultModal;
